import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Paper, Typography } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  ContentDialog,
  ContentDialogInit,
} from "../../../../components/MDDForm/components/ContentDialog.jsx";

const METHODS = [
  {
    key: "move",
    icon: SwapHorizIcon,
    label: "Aus anderem Vertrag zuordnen",
    description:
      "Ein Objekt, das aktuell einem anderen Vertrag dieses Kunden zugeordnet ist, hierher verschieben (Korrektur einer Fehlzuordnung).",
  },
  {
    key: "import",
    icon: UploadFileIcon,
    label: "Objekte über Datei-Upload aktualisieren",
    description:
      "Eine Excel-Objektliste hochladen, um Objekte für diesen Vertrag anzulegen oder zu aktualisieren.",
  },
];

/**
 * Vorschaltdialog für den Aktion-Menüpunkt "Objekte zuordnen" in der
 * Verträge-Tabelle der Kundendetailseite (ACC-321): lässt den Nutzer zwischen
 * "aus einem anderen Vertrag verschieben" (AssignObjectToContractDialog) und
 * "per Excel-Datei importieren" (ObjektImportPreviewDialog, ACC-318) wählen -
 * ein Menüpunkt statt zwei, Muster: AssignObjectsChoiceDialog.jsx (Bankkonten).
 */
function ObjectAssignmentChoiceDialog({ open, onClose, onContinue }) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();

  useEffect(() => {
    if (!open) {
      setOpenCD(false);
      return;
    }
    setSelectedMethod(null);
    setOpenCD(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setTca({
      title: "Objekte zuordnen",
      maxWidth: "sm",
      content: (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Wie möchten Sie Objekte diesem Vertrag zuordnen?
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            {METHODS.map(({ key, icon: Icon, label, description }) => (
              <Paper
                key={key}
                onClick={() => setSelectedMethod(key)}
                elevation={selectedMethod === key ? 4 : 1}
                sx={{
                  flex: 1,
                  p: 2,
                  cursor: "pointer",
                  border: 1,
                  borderColor:
                    selectedMethod === key ? "primary.main" : "grey.300",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    borderColor: "primary.light",
                    boxShadow: 3,
                  },
                }}
              >
                <Icon color={selectedMethod === key ? "primary" : "action"} />
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", mt: 1 }}
                >
                  {label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      ),
      actions: (
        <>
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={!selectedMethod}
            onClick={() => onContinue(selectedMethod)}
          >
            Weiter
          </Button>
        </>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod]);

  return (
    <ContentDialog
      openCD={openCD}
      setOpenCD={setOpenCD}
      tca={tca}
      onClose={onClose}
    />
  );
}

ObjectAssignmentChoiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

export default ObjectAssignmentChoiceDialog;
